import React, { useState } from 'react';
import { UserMenu, Logout, useUserMenu } from 'react-admin';
import { MenuItem, ListItemIcon, ListItemText, MenuItemProps, Divider, Radio } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import LanguageIcon from '@mui/icons-material/Language';
import ChangePasswordDialog from './ChangePasswordDialog';
import { useT, useAppLocale } from '../i18n/useT';
import { SUPPORTED_LOCALES, type AppLocale } from '../i18n/i18nProvider';

interface ChangePasswordMenuItemProps extends MenuItemProps {
    onOpenDialog: () => void;
}

const ChangePasswordMenuItem = React.forwardRef<HTMLLIElement, ChangePasswordMenuItemProps>((props, ref) => {
    const userMenuContext = useUserMenu();
    const { onOpenDialog, ...rest } = props;
    const t = useT();

    const handleClick = () => {
        onOpenDialog();
        userMenuContext?.onClose();
    };

    return (
        <MenuItem ref={ref} onClick={handleClick} {...rest}>
            <ListItemIcon>
                <LockResetIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('auth.changePassword.menuItem')}</ListItemText>
        </MenuItem>
    );
});

ChangePasswordMenuItem.displayName = 'ChangePasswordMenuItem';

const LocaleMenuItem = React.forwardRef<HTMLLIElement, MenuItemProps & { locale: AppLocale }>(
    ({ locale, ...rest }, ref) => {
        const userMenuContext = useUserMenu();
        const [current, setLocale] = useAppLocale();
        const t = useT();

        const handleClick = () => {
            if (locale !== current) setLocale(locale);
            userMenuContext?.onClose();
        };

        return (
            <MenuItem ref={ref} onClick={handleClick} {...rest} sx={{ pl: 5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <Radio
                        size="small"
                        checked={current === locale}
                        tabIndex={-1}
                        disableRipple
                        sx={{ p: 0 }}
                    />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
                    {t(`locale.${locale}` as Parameters<typeof t>[0])}
                </ListItemText>
            </MenuItem>
        );
    }
);

LocaleMenuItem.displayName = 'LocaleMenuItem';

const LanguageSectionHeader = React.forwardRef<HTMLLIElement, MenuItemProps>((props, ref) => {
    const t = useT();
    return (
        <MenuItem ref={ref} {...props} disabled sx={{ opacity: '1 !important' }}>
            <ListItemIcon>
                <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {t('locale.switch')}
            </ListItemText>
        </MenuItem>
    );
});

LanguageSectionHeader.displayName = 'LanguageSectionHeader';

export const CustomUserMenu: React.FC = () => {
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    return (
        <>
            <UserMenu>
                <ChangePasswordMenuItem onOpenDialog={() => setChangePasswordOpen(true)} />
                <Divider component="li" />
                <LanguageSectionHeader />
                {SUPPORTED_LOCALES.map(locale => (
                    <LocaleMenuItem key={locale} locale={locale} />
                ))}
                <Divider component="li" />
                <Logout />
            </UserMenu>
            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
            />
        </>
    );
};

